// Import utilities
const {
	isDefined
} = _uo.utility;

/**
 * Sanitizes the field attributes that come from the backend
 *
 * @param {Object} fieldAttributes Field attributes
 * @return {Object}                 Field attributes
 */
const sanitizeFieldAttributes = ( fieldAttributes ) => {
	// Deep clone attributes
	fieldAttributes = JSON.parse( JSON.stringify( fieldAttributes ) );

	// Sanitize data
	fieldAttributes = sanitizeField( fieldAttributes );

	// Check if it's a group or repeater field
	if ( [ 'group', 'repeater' ].includes( fieldAttributes.fieldType ) ) {
		// Iterate children
		fieldAttributes.fields = fieldAttributes.fields.map( ( childChildAttributes ) =>
			Object.assign(
				sanitizeField( childChildAttributes ),
				{
					// Inherit the item type to the children
					itemType: fieldAttributes.itemType
				}
			)
		);
	}

	return fieldAttributes;
};

export default sanitizeFieldAttributes;

/**
 * Converts the keys of the properties based on JS conventions
 *
 * @param {Object} fieldAttributes Field attributes
 * @return {Object}                 Field attributes
 */
const sanitizeField = ( fieldAttributes ) => {
	// Convert the name of the PHP properties to JS properties
	/* eslint-disable camelcase */ // Disabled because these come from the PHP, where this case is expected
	const properties = {
		required: 'isRequired',
		read_only: 'isReadOnly',
		supports_tinymce: 'supportsTinyMCE',
		supports_fullpage_editing: 'supportsFullPageEditing',
		copy_to_clipboard: 'supportsCopyToClipboard',
		is_ajax: 'isAjax',
		option_code: 'optionCode',
		input_type: 'fieldType',
		is_hidden: 'isHidden',
		token_name: 'tokenName',
		default_value: 'defaultValue',
		current_value: 'currentValue',
		max_length: 'maxLength',
		relevant_tokens: 'customFieldTokens',
		date_format: 'dateFormat',
		date_alt_format: 'dateAltFormat',
		supports_range: 'supportsRange',
		min_number: 'minNumber',
		max_number: 'maxNumber',
		min_date: 'minDate',
		max_date: 'maxDate',
		time_format: 'timeFormat',
		supports_tokens: 'supportsTokens',
		time_alt_format: 'timeAltFormat',
		min_time: 'minTime',
		max_time: 'maxTime',
		hide_actions: 'hideActions',
		supports_custom_value: 'supportsCustomValue',
		supports_multiple_values: 'supportsMultipleValues',
		custom_value_description: 'customValueDescription',
		fill_values_in: 'targetField',
		add_row_button: 'addRowButtonLabel',
		remove_row_button: 'removeRowButtonLabel',
		item_type: 'itemType',
		can_sort_rows: 'canSortRows',
		is_toggle: 'isToggle',
		options_show_id: 'optionsShowID',
		ajax: 'ajax',
		listen_fields: 'listenFields',
		clear_on_change_fields: 'clearOnChangeFields',
		show_label_in_sentence: 'showLabelInSentence',
		mapping_column: 'mappingColumn'
	};
	/* eslint-enable camelcase */

	// Iterate attributes
	Object.entries( fieldAttributes ).forEach( ( [ attributeKey, attributeValue ] ) => {
		// Check if we have to change this attribute
		if ( ! isDefined( properties[ attributeKey ] ) ) {
			return;
		}

		// Copy the value using the correct attribute key
		fieldAttributes[ properties[ attributeKey ] ] = attributeValue;

		// Check if it's an object with more properties inside
		try {
			if ( typeof attributeValue !== 'object' ) {
				throw Error();
			}

			Object.entries( attributeValue ).forEach( ( [ subAttributeKey, subAttributeValue ] ) => {
				// Check if we have to change this attribute
				if ( ! isDefined( properties[ subAttributeKey ] ) ) {
					return;
				}

				// Copy the value using the correct attribute key
				fieldAttributes[ properties[ attributeKey ] ][ properties[ subAttributeKey ] ] = subAttributeValue;

				// Remove old property
				delete fieldAttributes[ properties[ attributeKey ] ][ subAttributeKey ];
			} );
		} catch ( e ) {}

		// Remove old property
		if ( attributeKey !== properties[ attributeKey ] ) {
			delete fieldAttributes[ attributeKey ];
		}
	} );

	return fieldAttributes;
};
